import { getVoucherUnitPrice } from "../../Context/Voucher/VoucherContext";
import { calculateGST } from "../../Utils/Helper";

const toAmount = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
};

export const toOptionalAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount : null;
};

const roundMoney = (value) =>
  Math.round((toAmount(value) + Number.EPSILON) * 100) / 100;

export const formatMoney = (value, compactZero = false) => {
  const amount = roundMoney(value);
  if (compactZero && amount === 0) return "S$0";
  return `S$${amount.toFixed(2)}`;
};

const getDuration = (voucher) =>
  toAmount(
    voucher?.duration ??
      voucher?.voucher_duration ??
      voucher?.service_duration ??
      voucher?.product?.duration ??
      voucher?.voucher?.duration
  );

const getVoucherName = (voucher, fallback = "Voucher") =>
  String(
    voucher?.voucher_name ??
      voucher?.name ??
      voucher?.product_name ??
      voucher?.product?.name ??
      voucher?.voucher?.name ??
      fallback
  ).trim();

const getCategoryName = (voucher) =>
  String(
    voucher?.category_name ??
      voucher?.voucher_category_name ??
      voucher?.category?.name ??
      voucher?.product?.category_name ??
      voucher?.voucher?.category_name ??
      voucher?.voucher?.category?.name ??
      ""
  ).trim();

const getCategoryId = (voucher) =>
  voucher?.category_id ??
  voucher?.voucher_category_id ??
  voucher?.category?.id ??
  voucher?.product?.category_id ??
  voucher?.voucher?.category_id ??
  voucher?.voucher?.category?.id;

const getProductId = (voucher) =>
  voucher?.product_id ?? voucher?.voucher_id ?? voucher?.id;

const isServiceVoucher = (voucher) => {
  const searchableText = `${getCategoryName(voucher)} ${getVoucherName(voucher)}`;
  return getDuration(voucher) > 0 || /service|\bmins?\b/i.test(searchableText);
};

const formatCategoryName = (rawName, serviceVoucher) => {
  if (/cash/i.test(rawName)) return "Cash Voucher";
  if (/service/i.test(rawName)) return "Service Voucher";
  return rawName || (serviceVoucher ? "Service Voucher" : "Cash Voucher");
};

const formatVoucherName = (voucher) => {
  const name = getVoucherName(voucher);
  const duration = getDuration(voucher);
  if (duration > 0 && !/\bmins?\b/i.test(name)) {
    return `${duration} Mins ${name}`;
  }
  return name;
};

const getOrCreateCategory = (categories, voucher) => {
  const serviceVoucher = isServiceVoucher(voucher);
  const categoryName = formatCategoryName(
    getCategoryName(voucher),
    serviceVoucher
  );
  const categoryKey = String(getCategoryId(voucher) ?? categoryName);

  if (!categories.has(categoryKey)) {
    categories.set(categoryKey, {
      key: categoryKey,
      name: categoryName,
      isTaxable: serviceVoucher,
      itemsByKey: new Map(),
      explicitGst: 0,
      hasExplicitGst: false,
    });
  }

  const category = categories.get(categoryKey);
  category.isTaxable = category.isTaxable || serviceVoucher;
  return category;
};

const buildSelectedVoucherLookup = (selectedVouchers) => {
  const lookup = new Map();

  selectedVouchers.forEach((voucher) => {
    const productId = getProductId(voucher);
    if (productId === null || productId === undefined) return;
    const key = String(productId);
    const existing = lookup.get(key);
    lookup.set(key, existing ? { ...existing, ...voucher } : voucher);
  });

  return lookup;
};

const enrichOrderItems = (orderItems, selectedVouchers) => {
  const selectedLookup = buildSelectedVoucherLookup(selectedVouchers);

  return orderItems.map((item) => {
    const productId = getProductId(item);
    const selected =
      productId === null || productId === undefined
        ? null
        : selectedLookup.get(String(productId));

    if (!selected) return item;

    return {
      ...selected,
      ...item,
      product_id: item?.product_id ?? selected?.id ?? selected?.product_id,
      category_id: getCategoryId(item) ?? getCategoryId(selected),
      category_name: getCategoryName(item) || getCategoryName(selected),
      voucher_name: getVoucherName(item, "") || getVoucherName(selected),
      duration: getDuration(item) || getDuration(selected),
      price: item?.price ?? selected?.price,
      price_rate: item?.price_rate ?? selected?.price_rate,
    };
  });
};

const buildGroupsFromSelectedVouchers = (selectedVouchers) => {
  const categories = new Map();

  selectedVouchers.forEach((voucher) => {
    const category = getOrCreateCategory(categories, voucher);
    const itemName = formatVoucherName(voucher);
    const itemKey = String(
      getProductId(voucher) ?? `${category.key}:${itemName}`
    );
    const selectedCount = Math.max(1, Number(voucher?.count) || 1);
    const unitPrice = getVoucherUnitPrice(voucher);
    const existing = category.itemsByKey.get(itemKey);

    if (existing) {
      existing.occurrences += 1;
      existing.maxCount = Math.max(existing.maxCount, selectedCount);
      if (unitPrice > 0) existing.unitPrice = unitPrice;
    } else {
      category.itemsByKey.set(itemKey, {
        key: itemKey,
        name: itemName,
        occurrences: 1,
        maxCount: selectedCount,
        unitPrice,
      });
    }
  });

  return Array.from(categories.values()).map((category) => {
    const items = Array.from(category.itemsByKey.values()).map((item) => {
      const quantity = Math.max(item.occurrences, item.maxCount);
      return {
        key: item.key,
        name: item.name,
        quantity,
        lineTotal: roundMoney(item.unitPrice * quantity),
      };
    });

    return {
      key: category.key,
      name: category.name,
      isTaxable: category.isTaxable,
      items,
      itemsTotal: roundMoney(
        items.reduce((total, item) => total + item.lineTotal, 0)
      ),
      explicitGst: 0,
      hasExplicitGst: false,
    };
  });
};

const buildGroupsFromOrderItems = (orderItems) => {
  const categories = new Map();

  orderItems.forEach((voucher) => {
    const category = getOrCreateCategory(categories, voucher);
    const itemName = formatVoucherName(voucher);
    const itemKey = String(
      getProductId(voucher) ?? `${category.key}:${itemName}`
    );
    const quantity = Math.max(
      1,
      Number(voucher?.quantity ?? voucher?.count) || 1
    );
    const explicitLineTotal = toOptionalAmount(
      voucher?.subtotal ??
        voucher?.line_total ??
        voucher?.total_price ??
        voucher?.total
    );
    const unitPrice =
      toOptionalAmount(
        voucher?.unit_price ?? voucher?.price ?? voucher?.voucher_price
      ) ?? getVoucherUnitPrice(voucher);
    const lineTotal = roundMoney(explicitLineTotal ?? unitPrice * quantity);
    const existing = category.itemsByKey.get(itemKey);
    const explicitGst = toOptionalAmount(
      voucher?.gst_amount ?? voucher?.gst ?? voucher?.tax_amount
    );

    if (existing) {
      existing.quantity += quantity;
      existing.lineTotal = roundMoney(existing.lineTotal + lineTotal);
    } else {
      category.itemsByKey.set(itemKey, {
        key: itemKey,
        name: itemName,
        quantity,
        lineTotal,
      });
    }

    if (explicitGst !== null) {
      category.explicitGst = roundMoney(category.explicitGst + explicitGst);
      category.hasExplicitGst = true;
    }
  });

  return Array.from(categories.values()).map((category) => {
    const items = Array.from(category.itemsByKey.values());
    return {
      key: category.key,
      name: category.name,
      isTaxable: category.isTaxable,
      items,
      itemsTotal: roundMoney(
        items.reduce((total, item) => total + item.lineTotal, 0)
      ),
      explicitGst: category.explicitGst,
      hasExplicitGst: category.hasExplicitGst,
    };
  });
};

const addCategoryTotals = (groups, orderTotal) => {
  const preliminaryGroups = groups.map((group) => {
    const gstAmount = group.isTaxable
      ? group.hasExplicitGst
        ? group.explicitGst
        : toAmount(calculateGST(group.itemsTotal))
      : 0;

    return {
      ...group,
      gstAmount: roundMoney(gstAmount),
      subtotal: roundMoney(group.itemsTotal + gstAmount),
    };
  });
  const suppliedTotal = toOptionalAmount(orderTotal);
  const taxableGroups = preliminaryGroups.filter((group) => group.isTaxable);
  const allTaxableGroupsHaveExplicitGst =
    taxableGroups.length > 0 &&
    taxableGroups.every((group) => group.hasExplicitGst);

  if (
    suppliedTotal === null ||
    taxableGroups.length === 0 ||
    allTaxableGroupsHaveExplicitGst
  ) {
    return preliminaryGroups;
  }

  const calculatedTotal = roundMoney(
    preliminaryGroups.reduce((total, group) => total + group.subtotal, 0)
  );
  const roundingDifference = roundMoney(suppliedTotal - calculatedTotal);

  if (Math.abs(roundingDifference) > 0.05) return preliminaryGroups;

  const lastTaxableKey = taxableGroups[taxableGroups.length - 1].key;
  return preliminaryGroups.map((group) => {
    if (group.key !== lastTaxableKey) return group;
    const gstAmount = roundMoney(
      Math.max(0, group.gstAmount + roundingDifference)
    );
    return {
      ...group,
      gstAmount,
      subtotal: roundMoney(group.itemsTotal + gstAmount),
    };
  });
};

export const buildVoucherReceiptGroups = ({
  selectedVouchers = [],
  orderItems = [],
  orderTotal,
} = {}) => {
  const safeSelectedVouchers = Array.isArray(selectedVouchers)
    ? selectedVouchers
    : [];
  const safeOrderItems = Array.isArray(orderItems) ? orderItems : [];
  const groups =
    safeOrderItems.length > 0
      ? buildGroupsFromOrderItems(
          enrichOrderItems(safeOrderItems, safeSelectedVouchers)
        )
      : buildGroupsFromSelectedVouchers(safeSelectedVouchers);

  return addCategoryTotals(groups, orderTotal);
};
